/**
 * ContentState.js
 *
 * FSM handling events for content state.
 *
 * Copyright (c) apploft. GmbH
 */

import handleSubmit from './Create/handleSubmit';
import handleSelectSection from './Read/handleSelectSection';
import handleForm from './Create/handleForm';
import handleItemsReceived from './Read/handleItemsReceived';
import handleEdit from './Update/handleEdit';
import handleItemReceived from './Update/handleItemReceived';
import handleEditSubmit from './Update/handleEditSubmit';
import handleDelete from './Delete/handleDelete';
import handleFailedItemsRequest from './Read/handleFailedItemsRequest';
import handleFailedItemRequest from './Update/handleFailedItemRequest';

// States

import {
  ready,
  presentingList,
  presentingForm,
  fetchingItems,
  submittingForm,
  fetchingItem,
  presentingEditingForm,
} from './ContentStateSymbol';

// Events

import {
  failedItemsRequest,
  itemsReceived,
  form,
  formResponseReceived,
  submitForm,
  selectSection,
  failedFormSubmission,
  editItem,
  submitEditForm,
  deleteItem,
  itemReceived,
  failedItemRequest,
} from './ContentEventSymbol';

function init(api) {
  return {
    state: ready,
    data: `Welcome to ${api.info.title}`,
  };
}

/**
 * Routes actions through our state machine.
 *
 * This reducer is [idempotent](https://github.com/facebook/react/issues/16295).
 *
 * @param {*} state The current state.
 * @param {*} action The action to handle.
 */
function reducer({state, data, controller}, {event, payload}) {
  switch (state) {
    case submittingForm:
      controller.abort();

      switch (event) {
        case formResponseReceived:
          return handleSelectSection(payload);

        case failedFormSubmission:
          return {state: ready, data: payload};

        default:
          break;
      }

      break;

    case presentingEditingForm:
      switch (event) {
        case submitEditForm:
          return handleEditSubmit(data, payload);

        case deleteItem:
          return handleDelete(data, payload);

        case selectSection:
          return handleSelectSection(payload);

        default:
          break;
      }

      break;

    case ready:
      switch (event) {
        case selectSection:
          return handleSelectSection(payload);

        default:
          break;
      }

      break;

    case presentingForm:
      switch (event) {
        case selectSection:
          return handleSelectSection(payload);

        case submitForm:
          return handleSubmit(data, payload);

        default:
          break;
      }

      break;

    case presentingList:
      switch (event) {
        case selectSection:
          return handleSelectSection(payload);

        case form:
          return handleForm(data);

        case editItem:
          return handleEdit(payload);

        default:
          break;
      }

      break;

    case fetchingItem:
      controller.abort();

      switch (event) {
        case selectSection:
          return handleSelectSection(payload);

        case itemReceived:
          return handleItemReceived(payload);

        case failedItemRequest:
          return handleFailedItemRequest(payload);

        default:
          break;
      }

      break;

    case fetchingItems:
      controller.abort();

      switch (event) {
        case selectSection:
          return handleSelectSection(payload);

        case itemsReceived:
          return handleItemsReceived(payload);

        case failedItemsRequest:
          return handleFailedItemsRequest(payload);

        default:
          break;
      }

      break;

    default:
      break;
  }

  // In `React.StrictMode` repeated dispatches are possible, so we cannot
  // throw here as we would in a strict FSM.
  console.warn('passing state: ( %o, %o )', state, event);

  return {state, data, controller};
}

export {init, reducer};
